import React, { useEffect, useState } from "react";
import { Dropdown, Menu } from "antd";
import { MoreOutlined } from "@ant-design/icons";

/*
 * The single "⋮" overflow control used by the master listings (Employees Master,
 * Interns Master).
 *
 * `groups` is [{ title, actions: [{ key, icon, label, onSelect }] }]. Falsy
 * entries are dropped, and a group whose actions all disappear takes its heading
 * with it — so callers can build the list with plain `permission && {...}`
 * expressions and not worry about empty sections.
 */
const RowActionsMenu = ({ groups = [], label = "Row actions" }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    // The popup is rendered into <body>, so it does not travel with its row when
    // an ancestor scrolls — it would sit stranded over unrelated rows. Scroll
    // events do not bubble, but a capture-phase listener on window still sees
    // them from every scroller (page, layout content, the table's own x-scroll).
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      actions: (group.actions || []).filter(Boolean),
    }))
    .filter((group) => group.actions.length > 0);

  const actions = visibleGroups.reduce(
    (all, group) => all.concat(group.actions),
    []
  );

  if (actions.length === 0) return null;

  const menuItems = visibleGroups.map((group) => ({
    type: "group",
    key: `group-${group.title}`,
    label: group.title,
    children: group.actions.map((action) => ({
      key: action.key,
      label: (
        <span className="ema-menu-item">
          <span className="ema-menu-icon">{action.icon}</span>
          <span className="ema-menu-text">{action.label}</span>
        </span>
      ),
    })),
  }));

  return (
    <div className="ema-actions">
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        overlayClassName="ema-actions-dropdown"
        open={open}
        onOpenChange={setOpen}
        overlay={
          <Menu
            items={menuItems}
            onClick={({ key }) => {
              setOpen(false);
              const action = actions.find((item) => item.key === key);
              if (action) action.onSelect();
            }}
          />
        }
      >
        <button type="button" className="ema-actions-trigger" aria-label={label}>
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );
};

export default RowActionsMenu;
